/**
 * SVG sparkline showing convergence score history over time.
 */

import { useQuery } from '@tanstack/react-query';
import { getConvergenceHistory } from '@/lib/api';

interface ConvergenceChartProps {
  codebaseId: string;
  outcomeId: string;
}

function scoreColor(score: number): string {
  if (score >= 90) return '#50fa7b'; // success green
  if (score >= 70) return '#f1fa8c'; // warning yellow
  return '#ff5555'; // error red
}

export function ConvergenceChart({
  codebaseId,
  outcomeId,
}: ConvergenceChartProps): React.ReactElement | null {
  const { data } = useQuery({
    queryKey: ['convergence-history', codebaseId, outcomeId],
    queryFn: () => getConvergenceHistory(codebaseId, outcomeId),
    staleTime: 10_000,
  });

  const history = data?.history ?? [];
  if (history.length < 2) return null;

  const width = 200;
  const height = 40;
  const padding = 2;

  const scores = history.map(h => h.score);
  const minScore = Math.min(...scores, 0);
  const maxScore = Math.max(...scores, 100);
  const range = maxScore - minScore || 1;

  const points = scores.map((score, i) => {
    const x = padding + (i / (scores.length - 1)) * (width - padding * 2);
    const y = height - padding - ((score - minScore) / range) * (height - padding * 2);
    return { x, y, score };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const lastPoint = points[points.length - 1];
  const lastScore = lastPoint?.score ?? 0;

  return (
    <div className="space-y-1">
      <svg width={width} height={height} className="overflow-visible">
        {/* Reference lines */}
        <line
          x1={padding}
          y1={height - padding - (70 / range) * (height - padding * 2)}
          x2={width - padding}
          y2={height - padding - (70 / range) * (height - padding * 2)}
          stroke="#6272a4"
          strokeWidth="0.5"
          strokeDasharray="3,3"
          opacity="0.5"
        />
        <line
          x1={padding}
          y1={height - padding - (90 / range) * (height - padding * 2)}
          x2={width - padding}
          y2={height - padding - (90 / range) * (height - padding * 2)}
          stroke="#6272a4"
          strokeWidth="0.5"
          strokeDasharray="3,3"
          opacity="0.5"
        />
        {/* Score line */}
        <path
          d={pathD}
          fill="none"
          stroke={scoreColor(lastScore)}
          strokeWidth="1.5"
          opacity="0.8"
        />
        {/* Data points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2" fill={scoreColor(p.score)} opacity="0.9" />
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-text-tertiary">
        <span>{history.length} scores</span>
        <span>
          {scores[0]}% → {lastScore}%
        </span>
      </div>
    </div>
  );
}
